import Vue from 'vue';
import { componentLoader } from '@/tools/blockLoader';
import { createObjectFromArray, createObjectFromArrayByKey } from '@/tools/createObjectFromArray';
import { registerModule } from '../registerModule';
import toLeafLetGeoForms from './utils/toLeafLetGeoForms';
import { mutationTypes, actionTypes, errorTypes, articleTypes } from '@/types';
import { nativeMethodCall, initializeNativeInterface } from '@/tools/nativeInterface';
import logger from '@/tools/logger';

export default async function loadDataSource(
	{ commit, dispatch, state, getters },
	{ dataSourceURL, exhibitionID }
) {
	// Download the `datasource.cfg` file.
	let dataSourceCFG;
	const dsURL = `${dataSourceURL}${/\/$/.test(dataSourceURL) ? '' : '/'}`;
	const url = new URL(`./cache/datasource.cfg#${Date.now()}`, dsURL);

	try {
		dataSourceCFG = (await Vue.http.get(url.toString())).body;
	} catch (error) {
		dispatch(actionTypes.sendErrorMessage, { error: new Error(errorTypes.dataSourceConfigDownloadFailed) });
		return;
	}

	// Start observing connection state to cms and boxx;
	// dispatch(actionTypes.startObservingConnectionState); // FIXME this feature was not ready yet and was therefore removed for now

	// Create lookup objects that allow you to retrieve `beacon`s, `exhibition`s, `location`s
	// by their id. In the initial download of the data source the are transmitted as arrays.
	dataSourceCFG.beaconDescMap = createObjectFromArray(dataSourceCFG.beaconDescription);
	dataSourceCFG.exhibitionMap = createObjectFromArrayByKey(dataSourceCFG.exhibitions, 'exhibitionID');
	const locationMap = (dataSourceCFG.locations || []).reduce(
		(acc, location) =>
			Object.assign(acc, {
				[location._id]: Object.assign(location, { articles: [], tours: [], exhibitions: new Set() }),
			}),
		{}
	);

	commit(mutationTypes.setImageSize, {
		size: Number(dataSourceCFG.configWebClient.packageSyncer.pkgSyncImgSizes),
	});

	// Initialize Tours
	// Tours legacy fallback: if tours does not exist because
	// of legacy datasource create empty tours object
	if (!dataSourceCFG.tours) dataSourceCFG.tours = [];
	commit(mutationTypes.tourInit, {
		tours: dataSourceCFG.tours,
		exhibitionMap: dataSourceCFG.exhibitionMap,
	});

	// Initialize Tutorial
	commit(mutationTypes.tutorialInit, {
		exhibitions: dataSourceCFG.exhibitions,
		toursMap: state.tour.toursMap,
	});

	// Add the tours to each location so the search
	// under the map can show tours for locations
	// see `modules/search/actions/searchShowLocationsByList`
	dataSourceCFG.tours.forEach((tour) => {
		if (tour.startLocationID) locationMap[tour.startLocationID].tours.push(tour);
		if (tour.pathLocationID) locationMap[tour.pathLocationID].tours.push(tour);
	});
	// Create virtual pages in exhibitions
	dataSourceCFG.exhibitions.forEach((exhibition) => {
		// Add to every exhibition the overview article Meta Information.
		exhibition.languageContent = exhibition.languageContent.map((lc) => lc[1]); // make languageContent of Exhibitions same as articles
		exhibition.languages = exhibition.languageContent.map((lC) => lC.languageISO);
		exhibition.articles.push([
			articleTypes.articleList,
			{
				pageTheme: 'article-list',
				id: articleTypes.articleList,
				name: articleTypes.articleList,
				type: articleTypes.articleList,
				languageContent: exhibition.languageContent,
				triggers: [],
			},
		]);
		exhibition.articles.push([
			'tour-list',
			{
				pageTheme: 'tour-list',
				id: 'tour-list',
				name: 'tour-list',
				type: articleTypes.articleList,
				languageContent: exhibition.languageContent,
				triggers: [],
			},
		]);

		exhibition.articleMap = createObjectFromArray(exhibition.articles);

		if (typeof exhibition.geopoints === 'undefined') {
			// Initialize geopoints if not already set.
			exhibition.geopoints = [];
		}
		if (typeof exhibition.codes === 'undefined') {
			exhibition.codes = [];
		}
		if (typeof exhibition.beacons === 'undefined') {
			exhibition.beacons = [];
		}
		exhibition.geopoints.forEach(([, article]) => {
			article.exhibitionID = exhibition.exhibitionID;
		});
		exhibition.geopoints.forEach((keyVal) => {
			toLeafLetGeoForms(keyVal[1]);
		});

		exhibition.codes.forEach(([, article]) => {
			article.exhibitionID = exhibition.exhibitionID;
		});
		exhibition.beacons.forEach(([, article]) => {
			article.exhibitionID = exhibition.exhibitionID;
		});
		exhibition.geopointsMap = createObjectFromArray(exhibition.geopoints);
		exhibition.codeMap = createObjectFromArray(exhibition.codes);
		exhibition.beaconMap = createObjectFromArray(exhibition.beacons);

		// Add the landing article to the locations
		(exhibition.triggers || []).forEach(({ locationID }) => {
			locationMap[locationID].exhibitions.add(exhibition.exhibitionID);
			locationMap[locationID].articles.push({
				article: exhibition.articleMap[exhibition.landingArticleID],
				exhibitionID: exhibition.exhibitionID,
			});
		});
		// For each trigger of an `Article` in this `Exhibition`
		// add the article to the list of articles of the trigger `Location`.
		exhibition.articles.forEach(([, article]) => {
			(article.triggers || []).forEach(({ locationID, triggerType }) => {
				if (!locationMap[locationID]) {
					logger.warn(`missing location ${locationID}`);
					return;
				}
				locationMap[locationID].exhibitions.add(exhibition.exhibitionID);
				locationMap[locationID].articles.push({
					article: article,
					exhibitionID: exhibition.exhibitionID,
					triggerType,
				});
			});
		});
	});

	// Set the landing Exhibion.
	dataSourceCFG.landingExhibition = dataSourceCFG.global.landingExhibit
		? dataSourceCFG.exhibitionMap[dataSourceCFG.global.landingExhibit.exhibitionID]
		: dataSourceCFG.exhibitions[0];
	dataSourceCFG.locationMap = locationMap;
	if (!dataSourceCFG.landingExhibition)
		throw new Error('landingExhibition is not set corretly, please check the data source settings.');

	commit(mutationTypes.setDatasource, { dataSourceURL: dsURL, dataSourceCFG });
	// set swipe through history to active or stop swiping left right through history
	commit(
		mutationTypes.swipeEnableHistory,
		dataSourceCFG.configWebClient.swipeNavigation &&
			dataSourceCFG.configWebClient.swipeNavigation.enableHistorySwipe
	);

	// set page-transition-mode, if it is not yet set by the local dev-environment
	commit(mutationTypes.setPageTransitionMode, {
		transition:
			state.isApp &&
			dataSourceCFG.configWebClient.swipeNavigation &&
			dataSourceCFG.configWebClient.swipeNavigation.disableSwipeInBrowser
				? 'simple'
				: 'swipe',
	});

	// Init virtual root exhibition
	commit(mutationTypes.setRootExhibition);

	// Check if the device language is available,
	// if not set to fallback language as current language.
	if (!getters.possibleLanguages.includes(getters.currentLanguage)) {
		if (state.dataSourceCFG.exhibitionMap[exhibitionID]) {
			// if the exhibition does not exist
			commit(mutationTypes.setLanguage, {
				language: state.dataSourceCFG.exhibitionMap[exhibitionID].defaultLanguage,
			});
		} else {
			// Fallback Language can not estimated right now
			commit(mutationTypes.setLanguage, { language: getters.possibleLanguages[0] });
		}
	}

	// Initialize MQTT if the datasource has MQTT specified
	const {
		MQTTUserCredentials,
		MQTTTopicToSubscribe = {},
		mobile: mqttMobileSettings,
		mediaPlayers: mqttMediaPlayerSettings,
	} = state.dataSourceCFG.global.MQTTSupport;
	if (mqttMobileSettings.active || mqttMediaPlayerSettings.active) {
		let mqttSettings = mqttMobileSettings.active ? mqttMobileSettings : mqttMediaPlayerSettings;
		import(/* webpackChunkName: "tools/mqtt" */ '../modules/mqtt').then((mqttModule) => {
			registerModule('mqtt', mqttModule.default);
			dispatch(
				actionTypes.mqttConnect,
				Object.assign({}, mqttSettings, {
					username: MQTTUserCredentials.MQTTUsername ? MQTTUserCredentials.MQTTUsername : null,
					password: MQTTUserCredentials.MQTTPassword ? MQTTUserCredentials.MQTTPassword : null,
					dataSourceID: MQTTTopicToSubscribe.MQTTTopic
						? MQTTTopicToSubscribe.MQTTTopic
						: state.dataSourceCFG.dataSourceID,
				})
			).then(null, function (err) {
				logger.error('Mqtt Connection error:', err); // Error: "It broke"
			});
		});
	}
	// Initialize `nativeInterface` to to check if the webclient has to download packages.
	await initializeNativeInterface(state.isApp);
	// Show the app and ar version
	if (state.isApp) {
		nativeMethodCall({
			method: 'nativeAppGetOfflineAppsWithVersion',
			resultTransform: (result) => {
				if (!result) return;
				const apps = result ? JSON.parse(result) : [];
				const appsIndex = apps
					.map(({ name, version }) => ({
						name,
						version,
					}))
					.reduce((acc, { name, version }) => Object.assign(acc, { [name]: version }), {});
				const missingApps = dataSourceCFG.offlineAppWithVersionProviderInit.filter(({ name, version }) => {
					if (name === 'auto-exhibitions-list' || name === 'auto-exhibition-list') return false;
					if (name in appsIndex && appsIndex[name] !== version) {
						logger.error(
							`Package version missmatch ${name} expected version: app ${appsIndex[name]}, ds ${version}`
						);
					}
					return !(name in appsIndex);
				});
				if (missingApps.length) {
					dispatch(actionTypes.sendErrorMessage, {
						error: new Error(errorTypes.packageSyncerDownloadError),
					});
					logger.error('The following packages are missing:');
					missingApps.forEach(({ name, version }) => logger.error(`${name} version: ${version}`));
				}
			},
		});
		nativeMethodCall({
			method: 'nativeAppGetVersion',
			resultTransform: (result) => {
				logger.log(`%c Native App: ${result}`, 'text-align: center; font-size: 30px;');
			},
		});
		nativeMethodCall({
			method: 'nativeInfoGetAPIVersion',
			resultTransform: (result) => {
				logger.log(`%c Native API: ${result}`, 'text-align: center; font-size: 30px;');
			},
		});
		nativeMethodCall({
			method: 'nativeARGetInfo',
			resultTransform: (result) => {
				try {
					const resultObject = JSON.parse(result);
					logger.log(`%c AR Engine: ${resultObject.EngineVersion}`, 'text-align: center; font-size: 30px;');
					logger.log(`%c Unity: ${resultObject.UnityVersion}`, 'text-align: center; font-size: 30px;');
				} catch (error) {
					logger.error(`nativeARGetInfo parsing the result failed. ${error}`);
				}
			},
		});
	}

	if (dataSourceCFG.global.isProgressiveWebApp && !state.isPreview) {
		commit(mutationTypes.setImageSize, { size: 1024 });
		// Load the PWA manifest file that allows users to create a app icon on their phone
		document.head.innerHTML += `<link rel="manifest" href="${dataSourceURL}cache/manifest.webmanifest">`;
		// This must be set immediately since the first download buttons might render before the service
		// worker is loaded.
		commit(mutationTypes.packageSyncerSetIsSyncerApp, {
			mode: dataSourceCFG.configWebClient.packageSyncer.mode,
		});
		// Register service worker for progressive web app
		try {
			/* global NODE_ENV */
			if (NODE_ENV === 'production' && 'serviceWorker' in navigator) {
				window.addEventListener('load', async () => {
					navigator.serviceWorker.register(`./service-worker.js?dataSourceUrl=${dataSourceURL}`);
					dispatch(actionTypes.progressiveWebAppInit, {
						serviceWorkerRegistration: await navigator.serviceWorker.ready,
					});
				});
			}
		} catch (error) {
			logger.error('Progressive web app service worker could not be installed', error);
		}
	}

	// If this is a native app check if this is running on a loaner device
	if (state.isApp) commit(mutationTypes.nativeInterfaceUp); // FIXME move me to `nativeInterface.init`; Inform all native depending modules nativeInterface is up.
	if (
		state.isApp &&
		(await nativeMethodCall({
			method: 'nativeAppGetIsLoaner',
			fallback: () => false,
		}))
	) {
		commit(mutationTypes.isLoaner);
	}

	// Initialize the package syncer if it is avaible.
	if (state.isApp || state.isVirtualPackageSyncer) {
		await dispatch(actionTypes.packageSyncerInit);
	}

	// Check if Glossary and calendarEvents have to be loaded and load it
	// In case of packageSyncer both packages has to in the initial packages so they can already be loaded
	// FIXME: wait for calendar and glossary when search is fixed
	await dispatch(actionTypes.loadGlossary);
	await dispatch(actionTypes.loadCalendar);

	// Load custom js modules (webclient plugins)
	// try {
	// 	if (plugin) {
	// 		await dispatch(actionTypes.loadPlugin, {
	// 			pluginName: plugin.name,
	// 			url: plugin.url,
	// 		});
	// 	}
	// 	const pluginNames = (state.dataSourceCFG.global.customJsModules || []).filter((pluginName) =>
	// 		plugin ? pluginName !== plugin.name : pluginName
	// 	);

	// 	await Promise.all(pluginNames.map((pluginName) => dispatch(actionTypes.loadPlugin, { pluginName })));
	// } catch (error) {
	// 	logger.error('error downloadin plugins', error);
	// }
	// blocks if added to datasource
	const blockPackageNames = state.dataSourceCFG.global.blocks || [];

	for (let blockPackage of blockPackageNames) {
		// TODO: Case if more than one blockPackages is loaded this will fail
		let url = new URL(`apps/${blockPackage}/`, state.dataSourceURL);
		componentLoader.import(url.href);
	}
	if (state.editorApi.blocksUrl) {
		componentLoader.import(state.editorApi.blocksUrl);
	}
}
